import { useState, useEffect } from "react";
import { ArrowRight, ChevronDown, Zap } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/v2/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/v2/ui/popover";

const LANGUAGES = [
  "Hindi", "Kannada", "Telugu", "Tamil", "Marathi",
  "Malayalam", "Gujarati", "Bengali", "Odia",
];

const CITIES = [
  "Bangalore", "Mumbai", "Pune", "Hyderabad", "Delhi", "Chennai",
];

const SelfBookingSection = ({ bookingData }) => {

  const [selectedLang, setSelectedLang] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [langOpen, setLangOpen] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);

  // Listen for language selection from hero section
  useEffect(() => {
    const handler = (e) => {
      const lang = (e).detail;
      if (lang && LANGUAGES.includes(lang)) {
        setSelectedLang(lang);
      }
    };
    window.addEventListener("select-language", handler);
    return () => window.removeEventListener("select-language", handler);
  }, []);

  const isVirtual = selectedCity === "E-Pooja";

  const bookingUrl =
    selectedLang && selectedCity
      ? isVirtual
        ? `/${selectedLang.toLowerCase()}/e-pooja/pooja`
        : `/${selectedLang.toLowerCase()}/${selectedCity.toLowerCase()}/pooja`
      : null;

  return (
    <section id="self-booking" className="py-10 md:py-16 bg-secondary lg:hidden">
      <div className="container max-w-4xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-1.5 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-semibold mb-3">
            <Zap className="h-3.5 w-3.5" />
            Insta Book
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-2">
            {bookingData?.title?.split(bookingData?.highlight)[0]} <span className="text-primary">{bookingData?.highlight}</span> {bookingData?.title?.split(bookingData?.highlight)[1]}
          </h2>
          {(() => {
            const steps = bookingData?.steps || [
              { num: 1, label: "Select Preferences", hint: "Language & city" },
              { num: 2, label: "Choose Puja & Package", hint: "Compare options" },
              { num: 3, label: "Pick Date & Pay", hint: "Secure checkout" },
              { num: 4, label: "Priest at Your Door", hint: "Confirmed in 24hrs" },
            ];
            return (
              <motion.div
                className="flex flex-col md:flex-row items-start md:items-center justify-center gap-0 md:gap-4 mt-6 mb-4 mx-auto pl-4 md:pl-0"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={{ visible: { transition: { staggerChildren: 0.15 } } }}
              >
                {steps.map((step, i) => (
                  <motion.div
                    key={i}
                    className="flex flex-col items-start md:items-center"
                    variants={{
                      hidden: { opacity: 0, y: 20 },
                      visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
                    }}
                  >
                    <div className="flex items-center gap-3 md:flex-col md:gap-1.5">
                      <div className="w-8 h-8 rounded-full border-2 border-primary flex items-center justify-center text-primary font-bold text-sm shrink-0">
                        {step.num}
                      </div>
                      <div className="md:text-center min-w-0 text-left md:text-center">
                        <p className="w-full text-sm font-medium text-foreground text-left md:text-center">
                          {step.label}
                        </p>
                        <p className="w-full text-xs text-muted-foreground text-left md:text-center">
                          {step.hint}
                        </p>
                      </div>
                    </div>
                    {i < steps.length - 1 && (
                      <>
                        <div className="h-4 border-l-2 border-dashed border-primary/25 ml-[15px] md:hidden" />
                        <div className="w-8 border-t-2 border-dashed border-primary/25 hidden md:block" />
                      </>
                    )}
                  </motion.div>
                ))}
              </motion.div>
            );
          })()}
        </div>

        <div className="bg-background rounded-2xl border shadow-sm p-6 md:p-8 space-y-6">
          {/* Two side-by-side selectors */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Language Popover */}
            <Popover open={langOpen} onOpenChange={setLangOpen}>
              <PopoverTrigger asChild>
                <button 
                  aria-label={`Select language. Currently selected: ${selectedLang || "None"}`}
                  className={`w-full text-left rounded-xl p-3 bg-background transition-all group shadow-md hover:shadow-lg border ${selectedLang ? "border-primary" : "border-border/60 hover:border-primary/60"} ${langOpen ? "ring-2 ring-primary/20" : ""}`}
                >
                  <p className="text-xs font-medium text-muted-foreground mb-1">{bookingData?.languageLabel || "I prefer a priest who speaks"}</p>
                  <div className="flex items-center justify-between">
                    <span className={`text-base font-semibold ${selectedLang ? "text-primary" : "text-foreground/50"}`}>
                      {selectedLang || "Select language"}
                    </span>
                    <ChevronDown className={`h-5 w-5 text-muted-foreground group-hover:text-primary transition-transform ${langOpen ? "rotate-180" : ""}`} />
                  </div>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-4" align="start">
                <div className="flex flex-wrap gap-2">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang}
                      aria-label={`Choose ${lang}`}
                      onClick={() => { setSelectedLang(lang); setLangOpen(false); }}
                      className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all min-h-[40px] ${
                        selectedLang === lang
                          ? "bg-primary text-primary-foreground shadow-sm"
                          : "border border-border text-foreground hover:border-primary/50 hover:bg-primary/5"
                      }`}
                    >
                      {lang}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            {/* City Popover */}
            <Popover open={cityOpen} onOpenChange={setCityOpen}>
              <PopoverTrigger asChild>
                <button 
                  aria-label={`Select city. Currently selected: ${selectedCity || "None"}`}
                  className={`w-full text-left rounded-xl p-3 bg-background transition-all group shadow-md hover:shadow-lg border ${selectedCity ? "border-primary" : "border-border/60 hover:border-primary/60"} ${cityOpen ? "ring-2 ring-primary/20" : ""}`}
                >
                  <p className="text-xs font-medium text-muted-foreground mb-1">{bookingData?.cityLabel || "Pooja to be performed in"}</p>
                  <div className="flex items-center justify-between">
                    <span className={`text-base font-semibold ${selectedCity ? "text-primary" : "text-foreground/50"}`}>
                      {selectedCity === "E-Pooja" ? "🌐 E-Pooja (Virtual)" : selectedCity || "Select city"}
                    </span>
                    <ChevronDown className={`h-5 w-5 text-muted-foreground group-hover:text-primary transition-transform ${cityOpen ? "rotate-180" : ""}`} />
                  </div>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-96 p-4" align="start">
                <div className="flex flex-wrap gap-2">
                  {CITIES.map((city) => (
                    <button
                      key={city}
                      aria-label={`Choose ${city}`}
                      onClick={() => { setSelectedCity(city); setCityOpen(false); }}
                      className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all min-h-[40px] ${
                        selectedCity === city
                          ? "bg-primary text-primary-foreground shadow-sm"
                          : "border border-border text-foreground hover:border-primary/50 hover:bg-primary/5"
                      }`}
                    >
                      {city}
                    </button>
                  ))}
                  <button
                    aria-label="Choose E-Pooja (Virtual)"
                    onClick={() => { setSelectedCity("E-Pooja"); setCityOpen(false); }}
                    className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all min-h-[40px] ${
                      selectedCity === "E-Pooja"
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : "border border-border text-foreground hover:border-primary/50 hover:bg-primary/5"
                    }`}
                  >
                    🌐 E-Pooja (Virtual)
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-3 pt-2 border-t">
                  🌐 <strong>E-Pooja</strong> — Live or Recorded, for international users
                </p>
              </PopoverContent>
            </Popover>
          </div>

          {/* Live Preview */}
          {selectedLang && selectedCity && (
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 text-center">
              <p className="text-sm font-medium">
                {isVirtual ? (
                  <>Virtual pooja with a <strong className="text-primary">{selectedLang}</strong>-speaking priest</>
                ) : (
                  <>
                    <strong className="text-primary">{selectedLang}</strong>-speaking priest in{" "}
                    <strong className="text-primary">{selectedCity}</strong>
                  </>
                )}
              </p>
            </div>
          )}

          {/* Reassurance */}
          <p className="text-sm text-muted-foreground text-center">
            {bookingData?.reassurance || "Priest details & home prep list sent within 24 hrs · Partial payment accepted"}
          </p>

          {/* CTA */}
          <Button
            size="lg"
            className="w-full text-lg font-semibold min-h-[52px] rounded-xl whitespace-normal"
            disabled={!bookingUrl}
            asChild={!!bookingUrl}
          >
            {bookingUrl ? (
              <a href={bookingUrl} target="_blank" rel="noopener noreferrer">
                {bookingData?.buttonText || "Browse Puja Packages"} <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            ) : (
              <span>{bookingData?.disabledButtonText || "Select language & city first"}</span>
            )}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default SelfBookingSection;
